// NEW_FILE_JUSTIFIED: New page for Retell chat session detail — no existing chat detail page exists
/**
 * ChatSessionDetailPage
 *
 * Two-column layout: transcript/analysis tabs on the left, metadata sidebar on the right.
 * Supports "Create Lead" action from the sidebar.
 */

import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/ require three levels up to reach convex/_generated/
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  Clock,
  DollarSign,
  MessageSquare,
  Bot,
  UserPlus,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useChatSessionDetail, useCreateLeadFromChat } from '@/hooks/useRetellChats';
import type { IChatMessage } from '@/@types/retellChat';

function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDuration(ms: number | undefined): string {
  if (!ms) return '—';
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes === 0) return `${remainingSeconds}s`;
  return `${minutes}m ${remainingSeconds}s`;
}

function formatCost(cents: number | undefined): string {
  if (cents === undefined) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

function formatTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

const sentimentConfig: Record<string, { label: string; className: string }> = {
  Positive: { label: 'Positive', className: 'bg-green-500/10 text-green-600' },
  Neutral: { label: 'Neutral', className: 'bg-muted text-muted-foreground' },
  Negative: { label: 'Negative', className: 'bg-destructive/10 text-destructive' },
  Unknown: { label: 'Unknown', className: 'bg-muted text-muted-foreground' },
};

function TranscriptView({ messages }: { messages: IChatMessage[] }) {
  // LOCAL_TYPE_JUSTIFIED: inline prop type for private sub-component, no consumer outside this file
  const chatMessages = messages.filter(m => m.role === 'agent' || m.role === 'user');

  if (chatMessages.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p>No messages in this chat</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 py-4">
      {chatMessages.map(msg => (
        <div
          key={msg.messageId}
          className={cn(
            'flex gap-2 max-w-[85%]',
            msg.role === 'user' ? 'ml-auto flex-row-reverse' : ''
          )}
        >
          <div
            className={cn(
              'flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center',
              msg.role === 'agent'
                ? 'bg-primary/10 text-primary'
                : 'bg-violet-500/10 text-violet-600'
            )}
          >
            {msg.role === 'agent' ? (
              <Bot className="w-4 h-4" />
            ) : (
              <User className="w-4 h-4" />
            )}
          </div>
          <div
            className={cn(
              'rounded-lg px-3 py-2 text-sm',
              msg.role === 'agent'
                ? 'bg-muted text-foreground'
                : 'bg-primary text-primary-foreground'
            )}
          >
            <p className="whitespace-pre-wrap">{msg.content}</p>
            <p
              className={cn(
                'text-[10px] mt-1',
                msg.role === 'agent' ? 'text-muted-foreground' : 'text-primary-foreground/70'
              )}
            >
              {formatTime(msg.createdTimestamp)}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

export function ChatSessionDetailPage() {
  // 1. Hooks
  const { chatId } = useParams<{ chatId: string }>();
  const navigate = useNavigate();
  const [isCreatingLead, setIsCreatingLead] = useState(false);

  const sessionId = chatId as Id<'retellChatSessions'> | undefined; // AS_JUSTIFIED: one-time Id cast from route params
  const { data: session, isLoading } = useChatSessionDetail(sessionId);
  const createLead = useCreateLeadFromChat();

  // 2. Helpers
  const handleCreateLead = useCallback(async () => {
    if (!sessionId) return;
    setIsCreatingLead(true);
    try {
      const leadId = await createLead({ sessionId });
      if (leadId) {
        navigate(`/leads/${leadId}`);
      }
    } finally {
      setIsCreatingLead(false);
    }
  }, [sessionId, createLead, navigate]);

  // 3. Guard returns
  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          <Skeleton className="h-96" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Chat session not found.</p>
        <Button variant="ghost" className="mt-3" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
      </div>
    );
  }

  // 4. Computed (after guard returns narrow session to non-null)
  const sentiment = session.sentiment ? sentimentConfig[session.sentiment] : null;
  const chatMessageCount = session.messages.filter(
    m => m.role === 'user' || m.role === 'agent'
  ).length;
  const collectedVars =
    session.collectedVariables &&
    typeof session.collectedVariables === 'object'
      ? (session.collectedVariables as Record<string, unknown>)
      : null;

  // 5. Primary return
  return (
    <div className="min-h-full py-8 px-10 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          title="Back to chat history"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            {session.visitorName ?? 'Anonymous Visitor'}
          </h1>
          <p className="text-sm text-muted-foreground">{formatDate(session.startedAt)}</p>
        </div>
      </div>

      {/* Two-column layout: tabs + sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        {/* Main content tabs */}
        <div className="border border-border rounded-md overflow-hidden bg-card">
          <Tabs defaultValue="transcript">
            <TabsList className="w-full grid grid-cols-2 rounded-none border-b">
              <TabsTrigger value="transcript">Transcript</TabsTrigger>
              <TabsTrigger value="analysis">Analysis</TabsTrigger>
            </TabsList>

            <TabsContent value="transcript" className="m-0 p-4">
              <TranscriptView messages={session.messages} />
            </TabsContent>

            <TabsContent value="analysis" className="m-0 p-4 space-y-4">
              {session.chatSummary && (
                <div>
                  <h3 className="text-sm font-medium mb-1">Summary</h3>
                  <p className="text-sm text-muted-foreground">{session.chatSummary}</p>
                </div>
              )}
              <div>
                <h3 className="text-sm font-medium mb-1">Sentiment</h3>
                {sentiment ? (
                  <span
                    className={cn(
                      'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                      sentiment.className
                    )}
                  >
                    {sentiment.label}
                  </span>
                ) : (
                  <span className="text-sm text-muted-foreground">Not analyzed</span>
                )}
              </div>
              <div>
                <h3 className="text-sm font-medium mb-1">Outcome</h3>
                {session.chatSuccessful === true ? (
                  <span className="inline-flex items-center gap-1 text-sm text-green-600">
                    <CheckCircle className="w-4 h-4" /> Successful
                  </span>
                ) : session.chatSuccessful === false ? (
                  <span className="inline-flex items-center gap-1 text-sm text-destructive">
                    <XCircle className="w-4 h-4" /> Unsuccessful
                  </span>
                ) : (
                  <span className="text-sm text-muted-foreground">Not analyzed</span>
                )}
              </div>
              {session.toolCalls.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium mb-2">
                    Tool Calls ({session.toolCalls.length})
                  </h3>
                  <div className="space-y-2">
                    {session.toolCalls.map(tc => (
                      <div
                        key={tc.toolCallId}
                        className="rounded border p-2 text-xs font-mono bg-muted/50"
                      >
                        <p className="font-medium">{tc.name}</p>
                        {tc.successful !== undefined && (
                          <Badge
                            variant="outline"
                            className={cn(
                              'mt-1',
                              tc.successful
                                ? 'bg-green-500/10 text-green-600'
                                : 'bg-destructive/10 text-destructive'
                            )}
                          >
                            {tc.successful ? 'Success' : 'Failed'}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Chat Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Status</span>
                <Badge variant="outline">{session.status}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Duration</span>
                <span className="inline-flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatDuration(session.durationMs)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Cost</span>
                <span className="inline-flex items-center gap-1">
                  <DollarSign className="w-3 h-3" />
                  {formatCost(session.costCents)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Messages</span>
                <span>{chatMessageCount}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Visitor Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground" />
                <span>{session.visitorName ?? 'Anonymous'}</span>
              </div>
              {session.visitorEmail && (
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <span>{session.visitorEmail}</span>
                </div>
              )}
              {session.visitorPhone && (
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  <span>{session.visitorPhone}</span>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Lead</CardTitle>
            </CardHeader>
            <CardContent>
              {session.leadId ? (
                <div className="space-y-2">
                  <Badge className="bg-violet-500/10 text-violet-600 border-violet-200">
                    Lead linked
                  </Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => navigate(`/leads/${session.leadId}`)}
                  >
                    View Lead
                  </Button>
                </div>
              ) : (
                <Button
                  variant="default"
                  size="sm"
                  className="w-full"
                  onClick={() => void handleCreateLead()}
                  disabled={isCreatingLead}
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  {isCreatingLead ? 'Creating...' : 'Create Lead'}
                </Button>
              )}
            </CardContent>
          </Card>

          {collectedVars && Object.keys(collectedVars).length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Collected Data</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {Object.entries(collectedVars).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between">
                    <span className="text-muted-foreground">{key}</span>
                    <span className="font-mono text-xs">{String(value)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
